import { WebSocketMessage } from '../types/notification'

type WebSocketEventHandler = (message: WebSocketMessage) => void
type WebSocketStatusHandler = (connected: boolean) => void

class WebSocketClient {
  private ws: WebSocket | null = null
  private url: string
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectDelay = 1000
  private eventHandlers: Map<string, WebSocketEventHandler[]> = new Map()
  private statusHandlers: WebSocketStatusHandler[] = []
  private isConnecting = false
  private shouldReconnect = true
  private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null
  private connectPromise: Promise<void> | null = null

  constructor(baseUrl: string) {
    this.url = `${baseUrl.replace(/^http/, 'ws')}/ws`
  }

  connect(): Promise<void> {
    this.shouldReconnect = true

    if (this.ws?.readyState === WebSocket.OPEN) {
      return Promise.resolve()
    }

    if (this.connectPromise) {
      return this.connectPromise
    }

    this.connectPromise = new Promise((resolve, reject) => {
      this.isConnecting = true

      try {
        this.ws = new WebSocket(this.url)

        this.ws.onopen = () => {
          console.log('WebSocket connected')
          this.isConnecting = false
          this.connectPromise = null
          this.reconnectAttempts = 0
          this.notifyStatusHandlers(true)
          resolve()
        }

        this.ws.onmessage = (event) => {
          try {
            const message: WebSocketMessage = JSON.parse(event.data)
            this.handleMessage(message)
          } catch (error) {
            console.error('Error parsing WebSocket message:', error)
          }
        }

        this.ws.onclose = (event) => {
          console.log('WebSocket disconnected:', event.code, event.reason)
          this.isConnecting = false
          this.connectPromise = null
          this.notifyStatusHandlers(false)

          if (this.shouldReconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect()
          }
        }

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error)
          this.isConnecting = false
          this.connectPromise = null
          reject(error)
        }
      } catch (error) {
        this.isConnecting = false
        this.connectPromise = null
        reject(error)
      }
    })

    return this.connectPromise
  }

  private scheduleReconnect(): void {
    this.reconnectAttempts++
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1)

    console.log(`Scheduling WebSocket reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`)

    this.reconnectTimeoutId = setTimeout(() => {
      this.reconnectTimeoutId = null
      if (this.shouldReconnect) {
        this.connect().catch(console.error)
      }
    }, delay)
  }

  disconnect(): void {
    this.shouldReconnect = false
    this.isConnecting = false
    this.connectPromise = null
    if (this.reconnectTimeoutId !== null) {
      clearTimeout(this.reconnectTimeoutId)
      this.reconnectTimeoutId = null
    }
    if (this.ws) {
      this.ws.close()
      this.ws = null
    }
  }

  send(message: WebSocketMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message))
    } else {
      console.warn('WebSocket is not connected. Message not sent:', message)
    }
  }

  on(event: string, handler: WebSocketEventHandler): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, [])
    }
    this.eventHandlers.get(event)!.push(handler)
  }

  off(event: string, handler: WebSocketEventHandler): void {
    const handlers = this.eventHandlers.get(event)
    if (handlers) {
      const index = handlers.indexOf(handler)
      if (index > -1) {
        handlers.splice(index, 1)
      }
    }
  }

  onStatusChange(handler: WebSocketStatusHandler): void {
    this.statusHandlers.push(handler)
  }

  offStatusChange(handler: WebSocketStatusHandler): void {
    const index = this.statusHandlers.indexOf(handler)
    if (index > -1) this.statusHandlers.splice(index, 1)
  }

  private handleMessage(message: WebSocketMessage): void {
    const handlers = this.eventHandlers.get(message.type)
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(message)
        } catch (error) {
          console.error('Error in WebSocket event handler:', error)
        }
      })
    }

    // Also emit to wildcard listeners
    const wildcardHandlers = this.eventHandlers.get('*')
    if (wildcardHandlers) {
      wildcardHandlers.forEach(handler => {
        try {
          handler(message)
        } catch (error) {
          console.error('Error in WebSocket wildcard handler:', error)
        }
      })
    }
  }

  private notifyStatusHandlers(connected: boolean): void {
    this.statusHandlers.forEach(handler => {
      try {
        handler(connected)
      } catch (error) {
        console.error('Error in WebSocket status handler:', error)
      }
    })
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN
  }

  getConnectionState(): number {
    return this.ws?.readyState ?? WebSocket.CLOSED
  }
}

// Create singleton instance
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
export const wsClient = new WebSocketClient(API_BASE)

export default wsClient
